import { useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Grid, Result, Skeleton } from 'antd';
import { AppstoreOutlined, HomeOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ModuleLauncher, type ILauncherModule } from '@shared/common/components/ui/module-launcher';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { moduleGlyph } from '@/layouts/navigation';
import { trackEvent } from '@/lib/telemetry';

// Agent Studio's Modules drawer: the Provider Web module launcher (_Shared.Lib ui/module-launcher),
// fed by the resolved navigation. A tile opens the module's landing page; the active module then
// follows from the URL, so the rail narrows to that module's destinations. With five modules the
// shared launcher's own thresholds keep it a plain grid (search and dock appear at eight).
// Opening the drawer never navigates: a page's unsaved work is only at stake when a tile is chosen.

export const ModuleLauncherDrawer = observer(function ModuleLauncherDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { studioModuleStore: sm, themeStore } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [search, setSearch] = useState('');
  const isDark = themeStore.mode === 'dark';

  const placement = sm.placementFor(location.pathname);
  const currentKey = placement.kind === 'module' ? placement.module.key : null;

  const modules: ILauncherModule[] = sm.modules.map((m) => ({
    key: m.key,
    label: m.title,
    href: m.landing.to,
    glyph: moduleGlyph(m.iconKey),
    color: m.color ?? undefined,
    description: m.description ?? undefined,
    current: m.key === currentKey,
  }));

  const close = (): void => {
    setSearch('');
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      placement="left"
      size={screens.sm ? 520 : '100%'}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <AppstoreOutlined /> Modules
        </span>
      }
      styles={{
        body: {
          paddingTop: 16,
          // The shared launcher's label colours are for a light drawer; give it the dark surface's.
          ...(isDark
            ? ({ '--launcher-label': colors.slate[300], '--launcher-label-current': colors.slate[50], '--launcher-tile-hover': colors.slate[800], '--launcher-muted': colors.slate[400] } as React.CSSProperties)
            : {}),
        },
      }}
    >
      {sm.status === 'loading' && <Skeleton active paragraph={{ rows: 4 }} aria-label="Loading modules" />}

      {sm.status === 'error' && (
        <Result
          status="warning"
          title="Couldn't load your modules"
          subTitle={sm.error ?? 'The module catalog did not answer. Pages you open directly still work.'}
          extra={<Button icon={<ReloadOutlined />} onClick={() => void sm.load()}>Retry</Button>}
        />
      )}

      {sm.isStale && (
        <Alert
          type="warning"
          showIcon
          role="status"
          style={{ marginBottom: 16 }}
          message="These modules may be out of date"
          description={`Couldn't refresh them: ${sm.error}. They're the last ones loaded for you; pages still check access when you open them.`}
          action={
            <Button size="small" icon={<ReloadOutlined />} loading={sm.refreshing} onClick={() => void sm.load()}>
              Retry
            </Button>
          }
        />
      )}

      {sm.status === 'ready' && modules.length === 0 && (
        <Result status="info" title="No modules are available to you" subTitle="Ask an Agent Studio admin which pages your account can use." />
      )}

      {sm.status === 'ready' && modules.length > 0 && (
        <ModuleLauncher
          modules={modules}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search modules…"
          emptyText="No module matches."
          onOpen={(m, evt) => {
            // Let the browser open a new tab or window as usual; take over a plain click.
            if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button !== 0) return;
            evt.preventDefault();
            trackEvent('Modules.Opened', { module: String(m.key) });
            close();
            navigate(m.href);
          }}
        />
      )}

      <div
        style={{
          marginTop: 24,
          paddingTop: 12,
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Button icon={<HomeOutlined />} onClick={() => { close(); navigate('/'); }}>Home</Button>
        {sm.nav?.canManageModules && (
          <Button icon={<SettingOutlined />} onClick={() => { close(); navigate('/platform/modules'); }}>Manage modules</Button>
        )}
      </div>
    </Drawer>
  );
});
